import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FileText, Book, FlaskConical, GraduationCap, Calendar, Briefcase, Beaker, Mail, ArrowRight, Globe,
} from 'lucide-react';
import headshot from '../assets/bild.jpeg';

const navItems = [
  { key: 'cv', icon: FileText, link: '/cv' },
  { key: 'publications', icon: Book, link: '/publications' },
  { key: 'research', icon: FlaskConical, link: '/research' },
  { key: 'teaching', icon: GraduationCap, link: '/teaching' },
  { key: 'conferences', icon: Calendar, link: '/conferences' },
  { key: 'projects', icon: Briefcase, link: '/projects' },
  { key: 'experiments', icon: Beaker, link: '/experiments' },
  { key: 'contact', icon: Mail, link: '/contact' },
];

const Home = () => {
  const { t, i18n } = useTranslation();
  const tags = t('home.tags', { returnObjects: true });
  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'en' ? 'de' : 'en');

  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative bg-navy-900 text-[color:var(--on-navy)]">
        <button
          onClick={toggleLanguage}
          className="absolute right-6 top-5 z-10 flex items-center gap-1.5 rounded-sm border border-white/25 px-2.5 py-1 text-xs font-medium uppercase tracking-label text-[#A6C0DB] transition-colors hover:border-white/60 hover:text-[color:var(--on-navy)] md:right-10"
          aria-label={i18n.language === 'en' ? 'Auf Deutsch umschalten' : 'Switch to English'}
        >
          <Globe size={12} strokeWidth={1.75} />
          {i18n.language === 'en' ? 'DE' : 'EN'}
        </button>
        <div className="mx-auto flex max-w-5xl flex-col gap-10 px-6 py-16 md:flex-row md:items-center md:px-10 md:py-24">
          <div className="flex-1">
            <motion.h1
              className="font-display text-5xl font-semibold leading-[1.04] tracking-tight text-[color:var(--on-navy)] md:text-6xl"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {t('name')}
            </motion.h1>
            <motion.p
              className="mt-5 max-w-xl text-justify text-base leading-relaxed text-[#C6D6E6] md:text-lg"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.16 }}
            >
              {t('home.intro')}
            </motion.p>
            <motion.div
              className="mt-6 flex flex-wrap gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.28 }}
            >
              {Array.isArray(tags) &&
                tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-sm border border-white/25 bg-white/5 px-2.5 py-1 text-[11px] font-medium tracking-wide text-[#CBDBEC]"
                  >
                    {tag}
                  </span>
                ))}
            </motion.div>
          </div>

          <motion.img
            src={headshot}
            alt={t('home.headshot_alt')}
            className="h-40 w-40 flex-shrink-0 self-start rounded-[10px] object-cover shadow-xl ring-1 ring-white/10 md:h-52 md:w-52"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          />
        </div>
      </section>

      {/* ── Section index ────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 py-14 md:px-10 md:py-16">
        <p className="eyebrow mb-6">{t('home.sectionsLabel')}</p>
        <motion.div
          className="grid grid-cols-2 gap-4 md:grid-cols-4"
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
        >
          {navItems.map((item) => (
            <motion.div
              key={item.link}
              variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } }}
            >
              <Link
                to={item.link}
                className="group flex h-full min-h-[150px] flex-col justify-between rounded-md border border-line bg-beige-50 px-5 py-6 transition-colors duration-200 hover:border-navy-900 hover:bg-navy-900"
              >
                <div className="flex items-center justify-between">
                  <item.icon
                    size={20}
                    strokeWidth={1.6}
                    className="text-navy-600 transition-colors duration-200 group-hover:text-[#A6C0DB]"
                  />
                  <ArrowRight
                    size={14}
                    className="text-ink-400 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-[#A6C0DB]"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-ink-900 transition-colors duration-200 group-hover:text-[color:var(--on-navy)]">
                    {t(`nav.${item.key}`)}
                  </p>
                  <p className="mt-1 text-xs leading-snug text-ink-600 transition-colors duration-200 group-hover:text-[#A6C0DB]">
                    {t(`home.cardDesc.${item.key}`)}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>
    </div>
  );
};

export default Home;
